<template>
    <div
    class="details-container"
    :class="{
        'flat-top': flatTop,
        'mobile': getMobileAppState,
        'force-border-for-mobile': forceBorderForMobile,
        'force-full-border-for-mobile': forceFullBorderForMobile,
    }">
        <transition name="show-details" mode="out-in" @before-leave="beforeLeaveHandler">
            <!-- Accordion -->
            <div v-if="showDetails" class="accordion" :class="{ 'open-animation': isAccordion }">
                <!-- Details Elements Wrapper -->
                <div class="details-elements-wrapper" :class="{ 'border-bottom': isAccordion }">
                    <!-- Title -->
                    <div v-if="title" class="title">{{title}}</div>
                    <!-- Grid Helper -->
                    <div class="grid-helper-wrapper" :class="{'elements-in-one-column': elementsInOneColumn}">
                        <!-- Multiple Info -->
                        <div v-for="(entry, index) in entries" :key="index.key" class="v-for-helper">
                            <!-- Component -->
                            <component
                            v-if="entry.isComponent"
                            :is="entry.defineComponent"
                            :label="entry.label"
                            :content="entry.content"/>
                            <!-- Text Block -->
                            <text-block-generic
                            v-if="!entry.isComponent && !entry.defineComponent"
                            class="details-text-block"
                            :params="entry"
                            :applyEllipsis="entry.applyEllipsis"
                            :mask="entry.mask"/>
                        </div>
                    </div>
                </div>
            </div>
        </transition>
        <!-- Toggle Button -->
        <button v-if="isAccordion" @click="showDetails = !showDetails">
            {{showDetails ? t('textBlocks.hideDetails') : t('textBlocks.showDetails')}}
        </button>
    </div>
</template>

<script setup>
import TextBlockGeneric from '@/components/generic/textBlocks/Generic'
/* Vue */
import { ref, watch } from 'vue'
/* Store */
import { mapGetters } from '@/store/helpers/mappers'
/* i18n */
import { useI18n } from 'vue-i18n'
const { t } = useI18n()
/* mapGetters */
const { getMobileAppState } = mapGetters()

/* props received
@isAccordion { Boolean } - default is false, if true, component will have option of opening/closing
@flatTop { Boolean }
@forceBorderForMobile {Boolean} - prop to force border to be displayed in mobile (not default behaviour)
@forceFullBorderForMobile {Boolean} - prop to force full border to be displayed in mobile (not default behaviour)
@title {String} title for detail container
@entries { Array } each item is an Object
    - label { String }
	- content { String }
    - mask { Object }
    - isComponent { Boolean }
    - defineComponent { Function }
@elementsInOneColumn {Boolean} prop to form grid to only have one column
@identifier {Boolean} prop to identify this component, this is used to trigger some changes if needed, for example
*/
const props = defineProps({
	isAccordion: Boolean,
	flatTop: Boolean,
    forceBorderForMobile: Boolean,
    forceFullBorderForMobile: Boolean,
    title: String,
    entries: Array,
    elementsInOneColumn: Boolean,
    identifier: Boolean
})

const showDetails = ref(!props.isAccordion)

// This function toggles the accordion on identifier change if it's open
const toggleAccordion = () => {
    if (!showDetails.value) return
    showDetails.value = false
    setTimeout(() => { showDetails.value = true }, 750)
}

/* Since we never know the height, we will be giving the transition duration dynamicly */
const beforeLeaveHandler = el => {
	el.style['transition-duration'] = `${el.scrollHeight * 1.5}ms`
}

watch(() => props.identifier, (newVal, oldVal) => {
    if (props.isAccordion) toggleAccordion()
})

watch(() => props.title, (newVal, oldVal) => {
    if (props.isAccordion) toggleAccordion()
})
</script>

<style lang="scss" scoped>
.details-container {
    width: 100%;

    border-radius: 8px;
    border: solid 1px var(--secondary-light-2);

    &.flat-top {
        border-top-left-radius: 0;
        border-top-right-radius: 0;
    }

    .accordion {
        width: 100%;
        height: auto;
        overflow: hidden;

        .details-elements-wrapper {
            width: 100%;
            padding: 16px;

            &.border-bottom {
                border-bottom: solid 1px var(--secondary-light-2);
            }

            .title {
                width: 100%;
                margin-bottom: 12px;

                @include H6-Medium;
                color: var(--secondary-dark-2);
            }

            .grid-helper-wrapper {
                display: grid;
                grid-gap: 24px 26px;
                grid-template-columns: 1fr 1fr;

                &.elements-in-one-column {
                    grid-template-columns: 1fr;
                }

                .v-for-helper {
                    width: 100%;
                    overflow: hidden;

                    .text-block-info-wrapper {
                        max-width: 100%;
                    }
                }
            }

            // to overwrite .text-blocks-wrapper styles from OperationStepContentWrapper
            :deep(.text-blocks-wrapper) {
                &.details-text-block {
                    padding-right: 0;
                }
            }
        }

        &.open-animation {
            &.show-details-enter-active,
            &.show-details-leave-active {
                max-height: 1000px;
                opacity: 1;
                transition: all 800ms ease-in-out;
            }

            &.show-details-leave-to {
                max-height: 0;
                opacity: 0.2;
                transition: all 800ms linear;
            }

            &.show-details-enter-from {
                max-height: 0;
                opacity: 0.2;
                transition: all 800ms ease-in-out;
            }
        }
    }

    button {
        height: 50px;
        width: 100%;
        display: flex;
        align-items: center;
        justify-content: center;

        color: var(--primary);
        @include H6-Medium;
        text-transform: uppercase;
        cursor: pointer;
    }

    // Mobile Styles
    &.mobile {
        width: 100%;

        &:not(.force-full-border-for-mobile) {
            border: none;
            border-bottom: solid 1px var(--secondary);
        }

        &:not(.force-border-for-mobile):not(.force-full-border-for-mobile) {
            border: none;
        }

        .accordion {
            .details-elements-wrapper {
                .grid-helper-wrapper {
                    grid-template-columns: 1fr;
                }

                &.border-bottom {
                    border-bottom: none;
                }
            }
        }

        button {
            border-top: solid 1px var(--secondary);
        }
    }
}
</style>
